/**
 * Zweck: Beschreibt die TypeScript-Bausteine für den Frontend-Bereich frontend/src/app/modules/shop/components/view-action/shop.view-action.component.ts.
 */
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {View} from '../../../../models/view';
import {Artikel} from '../../../../models/artikel';
import {WarenkorbEntry} from '../../../../models/warenkorb-entry';
import {AppEventService} from '../../../../services/app.event.service';
import {BehaviorSubject, Subject, Subscription} from 'rxjs';
import {DomainVertreterConfig} from '../../../../models/domain-vertreter-config';
import {AppViewService} from '../../../../services/app.view.service';
import {AppUserService} from '../../../../services/app.user.service';
import {
  ShopWarenkorbEntryEditFieldsComponent
} from '../warenkorb-entry-edit-fields/shop.warenkorb-entry-edit-fields.component';
import {ShopWarenkorbButtonComponent} from '../warenkorb-button/shop.warenkorb-button.component';
import {finalize, takeUntil} from 'rxjs/operators';
import {ShopViewInterface} from '../../interfaces/shop.view-interface';
import {NotAvailableCheckerRequest} from '../../../not-available-checker/models/not-available-checker-request';
import {NotAvailableCheckerService} from '../../../not-available-checker/services/not-available-checker.service';
import {AppMessageService} from '../../../../services/app.message.service';

/**
 * Zweck: Beschreibt class ShopViewActionComponent und ihren Daten- oder Funktionsvertrag.
 */
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    // tslint:disable-next-line:component-selector
    selector: 'shop-view-action',
    templateUrl: 'shop.view-action.component.html',
    styleUrls: ['shop.view-action.component.scss'],
    standalone: false
})
export class ShopViewActionComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  @Input() viewcomp: ShopViewInterface;
  @Input() view: View;
  @Input() artikel: Artikel;
  @Input() islastavailable = false;
  @Input() inDetailsView = false;

  @Input() row_mode = false;

  disabled = false;
  entry = new WarenkorbEntry();


  vcfgsub: Subscription;
  vertreterconfig: BehaviorSubject<DomainVertreterConfig> = new BehaviorSubject<DomainVertreterConfig>(null);

  @ViewChild(ShopWarenkorbEntryEditFieldsComponent) editcomp: ShopWarenkorbEntryEditFieldsComponent;
  @ViewChild(ShopWarenkorbButtonComponent) wkbutton: ShopWarenkorbButtonComponent;

  _onDestroy = new Subject<void>();

  notAvailablePdfLoading = new BehaviorSubject<boolean>(false);
  showNotAvailableButton$: BehaviorSubject<boolean>;

/**
 * Beschreibt Methode constructor und ihren Rückgabevertrag.
 */
  constructor(private cd: ChangeDetectorRef, private esvc: AppEventService, private vsvc: AppViewService,
              public usvc: AppUserService, private notAvailableChecker: NotAvailableCheckerService,
              private msg: AppMessageService) {
    this.showNotAvailableButton$ = this.vsvc.showNotAvailableButton;
  }

/**
 * Beschreibt Methode generateNotAvailablePdf und ihren Rückgabevertrag.
 */
  generateNotAvailablePdf(): void {
    if (this.notAvailablePdfLoading.value) {
      return;
    }

    const today = new Date();
    const request = new NotAvailableCheckerRequest();
    request.docDate = today;
    request.queryDate = today;
    request.articles = [this.artikel];
    this.notAvailablePdfLoading.next(true);

    this.notAvailableChecker.generate(request).pipe(
      takeUntil(this._onDestroy),
      finalize(() => this.notAvailablePdfLoading.next(false))
    ).subscribe({
      error: () => this.msg.error('Das Nichtverfügbarkeits-PDF konnte nicht erzeugt werden.')
    });
  }

/**
 * Beschreibt Methode ngOnChanges und ihren Rückgabevertrag.
 */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.view) {
      this.entry.view_slug = this.view.slug;
    }
  }



/**
 * Beschreibt Methode getView und ihren Rückgabevertrag.
 */
  getView() {
    let view = this.view;

    if (this.artikel.view) {
      view = this.artikel.view;
    }

    return view;
  }

  /**
   * Prüft die Ausblendung eines Buttons in der wirksamen Shop-Ansicht.
   */
  isActionHidden(action: string): boolean {
    const view = this.getView();
    return !!(view && view.settings && view.settings.hidden_actions
      && view.settings.hidden_actions.includes(action));
  }

/**
 * Beschreibt Methode getViewSlug und ihren Rückgabevertrag.
 */
  getViewSlug() {
    let slug = this.view ? this.view.slug : null;

    if (this.artikel.view) {
      slug = this.artikel.view.slug;
    }

    return slug;
  }

  private detectChanges() {
    if (!this.cd['destroyed']) {
      this.cd.detectChanges();
    }
  }

/**
 * Beschreibt Methode focusMenge und ihren Rückgabevertrag.
 */
  focusMenge() {
    this.editcomp.focusMenge();
  }

/**
 * Beschreibt Methode mengeEnter und ihren Rückgabevertrag.
 */
  mengeEnter() {
    if (this.wkbutton) {
      this.wkbutton.addToPrimaryWarenkorb();
    } else if (this.viewcomp) {
      this.viewcomp.focusNextMenge(this);
    }
  }

/**
 * Beschreibt Methode change und ihren Rückgabevertrag.
 */
  change() {
    this.esvc.addGuiEvent('view_article_changed', this.artikel.ArtikelNummer);
  }

/**
 * Beschreibt Methode ngAfterViewInit und ihren Rückgabevertrag.
 */
  ngAfterViewInit(): void {
  }

/**
 * Beschreibt Methode reset und ihren Rückgabevertrag.
 */
  reset() {
    const entry = JSON.parse(JSON.stringify(this.entry));
    if (this.view.settings.bestellblock_mode) {
      entry.menge = 0;
    } else {
      entry.menge = 1;
    }
    entry.positionsart = 'Standard';
    entry.artikel = this.artikel;
    entry.view_slug = this.view.slug;
    entry.basisverrechnungspreis = this.artikel.Kundeneinkaufspreis;
    entry.freimenge = undefined;
    entry.aktionsrabattmenge = undefined;
    entry.naturalrabattmenge = undefined;
    entry.aktionsrabattprozent = undefined;
    entry.basisverrechnungspreisrabattprozent = 0;
    this.entry = entry;
    this.cd.markForCheck();
    this.change();
  }

/**
 * Beschreibt Methode ngOnDestroy und ihren Rückgabevertrag.
 */
  ngOnDestroy(): void {
    if (this.viewcomp) {
      this.viewcomp.unregisterViewActionComponent(this);
    }
    this._onDestroy.next();
    this._onDestroy.complete();
    if (this.vcfgsub instanceof Subscription) {
      this.vcfgsub.unsubscribe();
    }
  }

/**
 * Beschreibt Methode ngOnInit und ihren Rückgabevertrag.
 */
  ngOnInit(): void {
    this.esvc.getQueue().pipe(takeUntil(this._onDestroy)).subscribe(e => {
      if (e.name == 'gui.shop_reset_entry_fields' && e.data.ArtikelNummer == this.artikel.ArtikelNummer) {
        this.reset();
      }
      if (e.name == 'gui.addWkEntryArtikelMenge' && e.data.artnr == this.artikel.ArtikelNummer
        && !(this.viewcomp && this.viewcomp.sub_route_active)) {
        const menge = Number(e.data.menge);
        if (!this.disabled && this.wkbutton && menge > 0) {
          this.entry.menge = menge;
          this.esvc.addGuiEvent('setWkEntryArtikelMenge', {
            artnr: this.artikel.ArtikelNummer,
            menge: menge
          });
          this.wkbutton.addToPrimaryWarenkorb(undefined, e.data.origin);
        }
      }
    });
    if (this.viewcomp) {
      this.viewcomp.registerViewActionComponent(this);
    }
    this.vcfgsub = this.vsvc.$vertreterconfig.subscribe(c => {
      this.vertreterconfig.next(c);
    });
    if (this.view && this.view.settings && this.view.settings.bestellblock_mode) {
      this.entry.menge = 0;
      this.entry.freimenge = 0;
    } else {
      this.entry.menge = 1;
      this.entry.freimenge = 0;
    }
    this.entry.artikel = this.artikel;
    if (this.view) {
      this.entry.view_slug = this.view.slug;
    }
    this.entry.basisverrechnungspreis = this.artikel.Kundeneinkaufspreis;

    if (['NE', 'AV', 'NG'].includes(this.artikel.VerfuegbarkeitsstatusCode)) {
      this.disabled = true;
      /*} else if (this.artikel.IstArzneimittel && !this.artikel.HatBezugBerechtigung) {
        this.disabled = true;*/
    } else {
      this.disabled = false;
    }

    if (this.islastavailable) {
      this.disabled = false;
    }
  }
}
